import type { SnapshotStatusResponse } from "../types/inpact";
import { describeApiError } from "../lib/api";

interface SnapshotStatusProps {
  status: SnapshotStatusResponse | null;
  isLoading?: boolean;
  isRefreshing?: boolean;
  statusError?: Error | null;
  refreshError?: Error | null;
  onRefresh?: () => void;
}

function ageLabel(ageSeconds: number | null): string {
  if (ageSeconds === null) return "Not updated yet";
  if (ageSeconds < 60) return "Updated less than a minute ago";
  const minutes = Math.floor(ageSeconds / 60);
  return `Updated ${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
}

function stateMessages(status: SnapshotStatusResponse): string[] {
  const messages: string[] = [];
  if (status.state === "refreshing") {
    messages.push(status.refreshed_at
      ? "Refreshing data. The previous snapshot remains displayed."
      : "Loading the first snapshot.");
  }
  if (status.error?.code === "core_refresh_failed") {
    messages.push(status.refreshed_at
      ? "Refresh failed. The previous snapshot remains displayed."
      : "Snapshot unavailable. Data cannot be displayed yet.");
  } else if (status.state === "failed") {
    messages.push("Snapshot unavailable. Data cannot be displayed yet.");
  }
  if (status.state === "cold") messages.push("Snapshot not available yet.");
  if (status.is_stale && status.refreshed_at) {
    messages.push("Data may be out of date.");
  }
  if (status.state === "degraded") {
    messages.push(status.unavailable_objects.length > 0
      ? `Some related data is unavailable: ${status.unavailable_objects.join(", ")}.`
      : "Some related data is unavailable.");
  }
  return messages;
}

export function SnapshotStatus({
  status,
  isLoading = false,
  isRefreshing = false,
  statusError = null,
  refreshError = null,
  onRefresh,
}: SnapshotStatusProps) {
  if (isLoading && !status) {
    return <div className="snapshot-status" role="status">Loading data freshness…</div>;
  }
  if (!status) {
    return (
      <div className="snapshot-status snapshot-status--warning" role="status">
        {statusError ? "Snapshot status unavailable." : "Snapshot not available yet."}
        {statusError && (
          <span className="snapshot-status__error" role="alert">
            Status check failed: {describeApiError(statusError)}
          </span>
        )}
      </div>
    );
  }

  const messages = stateMessages(status);
  const activeRefresh = isRefreshing || status.state === "refreshing";
  return (
    <div
      className={`snapshot-status snapshot-status--${status.state}`}
      role="status"
      aria-live="polite"
    >
      <span className="snapshot-status__age">{ageLabel(status.age_seconds)}</span>
      {messages.map((message) => (
        <span className="snapshot-status__message" key={message}>{message}</span>
      ))}
      {statusError && (
        <span className="snapshot-status__error" role="alert">
          Status check failed: {describeApiError(statusError)}
        </span>
      )}
      {refreshError && (
        <span className="snapshot-status__error" role="alert">
          Refresh request failed: {describeApiError(refreshError)}
        </span>
      )}
      {status.can_refresh && onRefresh && (
        <button
          type="button"
          className="snapshot-status__refresh"
          onClick={onRefresh}
          disabled={activeRefresh}
          aria-label="Refresh data"
        >
          {activeRefresh ? "Refreshing…" : "Refresh"}
        </button>
      )}
    </div>
  );
}
